/**
 * Mixpanel browser SDK wrapper.
 *
 * All functions are silent no-ops when VITE_MIXPANEL_TOKEN is unset (dev / tests / CI).
 * `initMixpanel()` **dynamically imports** the SDK once and stashes the module; the track/identify
 * helpers then use that reference synchronously. Because `if (!TOKEN)` is a build-time constant,
 * the dynamic import is tree-shaken out of the build entirely when no token is configured — so
 * mixpanel-browser costs nothing unless telemetry is enabled.
 *
 * Import from "../analytics" (the public API) rather than directly from this file.
 */
import type { Dict } from "mixpanel-browser";

const TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;

/** The loaded SDK (null until initMixpanel resolves; stays null when no token). */
let _mp: typeof import("mixpanel-browser") | null = null;

export function initMixpanel(): void {
  if (!TOKEN) return; // build-time constant ⇒ the dynamic import below is tree-shaken when unset
  void (async () => {
    try {
      const mod = await import("mixpanel-browser");
      const mp = (mod.default ?? mod) as unknown as typeof import("mixpanel-browser");
      mp.init(TOKEN, { persistence: "localStorage", track_pageview: false });
      _mp = mp;
    } catch {
      // mixpanel-browser failed to load — no-op
    }
  })();
}

export function mpTrack(event: string, props?: Record<string, unknown>): void {
  try {
    _mp?.track(event, props as Dict | undefined);
  } catch {
    // never raise
  }
}

export function mpIdentify(userId: string, traits?: Record<string, unknown>): void {
  try {
    if (!_mp) return;
    _mp.identify(userId);
    if (traits && Object.keys(traits).length > 0) {
      _mp.people.set(traits as Dict);
    }
  } catch {
    // never raise
  }
}

export function mpSetUserProps(props: Record<string, unknown>): void {
  try {
    _mp?.people.set(props as Dict);
  } catch {
    // never raise
  }
}

export function mpReset(): void {
  try {
    _mp?.reset();
  } catch {
    // never raise
  }
}
